import axios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from "axios";
import { isPublicRoute } from "../route/publicRouteUtils";

export interface ApiError {
  errors: { message: string }[];
  formErrors: object;
  id: string;
  type: string;
  message?: string;
}

export interface ApiResponse<T> extends AxiosResponse<T> {
  ok: () => boolean;
  getError: () => string | object | null;
  getFullError: () => object | null;
  isCancel?: () => boolean;
}

// ─── Token helpers ────────────────────────────────────────────────────────────
// Treat the string "undefined" (written by old code) the same as a missing key.

const getToken = (key: string): string | null => {
  const value = localStorage.getItem(key);
  return value && value !== "undefined" ? value : null;
};

const clearTokens = () => {
  localStorage.removeItem("xgs-access-token");
  localStorage.removeItem("xgs-id-token");
  localStorage.removeItem("xgs-refresh-token");
  localStorage.removeItem("xgs-impersonate-token");
};

// ─── In-flight refresh promise ────────────────────────────────────────────────
// A single shared promise so that concurrent 401s all wait for the same refresh
// instead of firing N parallel refresh requests.

let refreshPromise: Promise<string> | null = null;

class Api {
  private static instance: AxiosInstance;

  private constructor() {}

  private static refreshToken = (): Promise<string> => {
    // Return the in-flight promise if one already exists.
    if (refreshPromise) return refreshPromise;

    const refreshTokenValue = getToken("xgs-refresh-token");

    if (!refreshTokenValue) {
      return Promise.reject(new Error("No refresh token available"));
    }

    const request = {
      grant_type: "refresh_token",
      client_id: process.env.REACT_APP_SSO_CLIENT_ID || "",
      scope: "openid",
      refresh_token: refreshTokenValue,
    };

    const params = new URLSearchParams(request).toString();

    refreshPromise = Api.instance
      .post(process.env.REACT_APP_TOKEN_ENDPOINT || "", params, {
        headers: { "Content-type": "application/x-www-form-urlencoded" },
        // Prevent the response interceptor from trying to refresh again
        // if this request itself returns 401.
        _skipRefresh: true,
      } as any)
      .then((rs: any) => {
        const { access_token, id_token, refresh_token } = rs.data;
        localStorage.setItem("xgs-access-token", access_token);
        localStorage.setItem("xgs-id-token", id_token);
        localStorage.setItem("xgs-refresh-token", refresh_token);
        return access_token as string;
      })
      .finally(() => {
        // Always clear the shared promise so the next expiry triggers a fresh refresh.
        refreshPromise = null;
      });

    return refreshPromise;
  };

  // ─── Redirect to SSO login ─────────────────────────────────────────────────

  private static redirectToLogin = () => {
    clearTokens();
    const redirectUrl =
      process.env.REACT_APP_DOMAIN +
      `/auth-callback?originUrl=${process.env.REACT_APP_DOMAIN + "/"}`;
    const authUrl =
      process.env.REACT_APP_AUTH_ENDPOINT +
      `?client_id=${process.env.REACT_APP_SSO_CLIENT_ID}&response_type=code&scope=openid&redirect_uri=${redirectUrl}`;
    window.location.replace(authUrl);
  };

  // ─── Singleton factory ─────────────────────────────────────────────────────

  public static getInstance = () => {
    if (!Api.instance) {
      Api.instance = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
        withCredentials: true,
      });

      // ── Request interceptor ──────────────────────────────────────────────
      Api.instance.interceptors.request.use(
        (config: AxiosRequestConfig) => {
          // Impersonate token takes priority; fall back to regular access token.
          const impersonateToken = getToken("xgs-impersonate-token");
          const accessToken = getToken("xgs-access-token");

          const authHeaderValue = impersonateToken
            ? `Bearer ${impersonateToken}`
            : accessToken
            ? `Bearer ${accessToken}`
            : "none";

          config.headers.Authorization = authHeaderValue;
          return config;
        },
        (error) => Promise.reject(error)
      );

      // ── Response interceptor ─────────────────────────────────────────────
      Api.instance.interceptors.response.use(
        // ── Success path ───────────────────────────────────────────────────
        (response) => {
          const apiResponse = response as ApiResponse<typeof response.data>;
          apiResponse.ok = () =>
            [200, 201, 204].includes(apiResponse.status);
          return apiResponse;
        },

        // ── Error path ─────────────────────────────────────────────────────
        async (error) => {
          const originalConfig = error.config as AxiosRequestConfig & {
            _retry?: boolean;
            _skipRefresh?: boolean;
          };
          const apiResponse = error.response as ApiResponse<ApiError> | undefined;

          // Network error / cancelled request — no response object.
          if (!apiResponse) {
            return {
              ok: () => false,
              isCancel: () => axios.isCancel(error),
            };
          }

          // ── Silent token refresh on 401 ──────────────────────────────────
          // Skip if:
          //   • Already retried this exact request (prevents infinite loop).
          //   • The failing request is itself the refresh endpoint.
          //   • We have no refresh token to work with.
          const hasRefreshToken = !!getToken("xgs-refresh-token");

          if (
            apiResponse.status === 401 &&
            !originalConfig._retry &&
            !originalConfig._skipRefresh &&
            hasRefreshToken
          ) {
            // Impersonate token is no longer valid — drop it so the real token is used.
            localStorage.removeItem("xgs-impersonate-token");

            originalConfig._retry = true;

            try {
              const newAccessToken = await Api.refreshToken();
              // Patch the Authorization header on the original request and retry.
              originalConfig.headers.Authorization = `Bearer ${newAccessToken}`;
              return Api.instance(originalConfig);
            } catch (refreshError) {
              // Refresh failed (expired / revoked refresh token) → force re-login on private pages.
              if (!isPublicRoute(window.location.pathname)) {
                Api.redirectToLogin();
              }
              return Promise.reject(refreshError);
            }
          }

          // ── 401 with no refresh token → redirect to login (private pages only) ──
          if (
            apiResponse.status === 401 &&
            !hasRefreshToken &&
            !isPublicRoute(window.location.pathname)
          ) {
            Api.redirectToLogin();
          }

          // ── Decorate the error response with helpers ──────────────────────
          apiResponse.ok = () => false;

          apiResponse.getError = () => {
            const apiError = apiResponse.data;
            return apiError?.errors?.[0]?.message
              ?? apiError?.message
              ?? "Error! Try again later or contact customer support.";
          };

          apiResponse.getFullError = () => {
            const apiError = apiResponse.data;
            return apiError?.errors
              ? {
                  status: apiResponse.status,
                  formErrors: apiError.formErrors,
                  errors: apiError.errors[0]?.message ?? null,
                }
              : null;
          };

          console.error(apiResponse);
          return apiResponse;
        }
      );
    }

    return Api.instance;
  };
}

export default Api.getInstance();